import { useEffect, useRef, useState } from 'react'

export interface IWindowSize {
  width: number
  height: number
}

export interface IUseWindowSizeOptions {
  debounceMs?: number
}

const getWindowSize = (): IWindowSize => ({
  width: window.innerWidth,
  height: window.innerHeight,
})

const listeners = new Set<(size: IWindowSize) => void>()
let isAttached = false

const handleWindowChange = () => {
  const size = getWindowSize()
  listeners.forEach((listener) => listener(size))
}

const subscribe = (listener: (size: IWindowSize) => void) => {
  if (!isAttached) {
    window.addEventListener('resize', handleWindowChange)
    window.addEventListener('orientationchange', handleWindowChange)
    isAttached = true
  }
  listeners.add(listener)

  return () => {
    listeners.delete(listener)
    if (listeners.size === 0 && isAttached) {
      window.removeEventListener('resize', handleWindowChange)
      window.removeEventListener('orientationchange', handleWindowChange)
      isAttached = false
    }
  }
}

export const useWindowSize = ({ debounceMs }: IUseWindowSizeOptions = {}): IWindowSize => {
  const [liveSize, setLiveSize] = useState<IWindowSize>(getWindowSize)
  const [debouncedSize, setDebouncedSize] = useState<IWindowSize>(liveSize)
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const isFirstDebounceRun = useRef(true)

  useEffect(() => subscribe(setLiveSize), [])

  useEffect(() => {
    if (!debounceMs) return undefined

    if (isFirstDebounceRun.current) {
      isFirstDebounceRun.current = false
      return undefined
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => setDebouncedSize(liveSize), debounceMs)

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveSize.width, liveSize.height, debounceMs])

  return debounceMs ? debouncedSize : liveSize
}
